import React, { useEffect, useMemo, useState } from "react";
import { ReactComponent as PercentLogo } from "../../../assets/images/percentMapLogo.svg";
import { i18n, type Lang } from "../i18n"; // ✅ 네 프로젝트 경로에 맞게 조정

function classNames(...arr: Array<string | false | undefined | null>) {
    return arr.filter(Boolean).join(" ");
}

/** ===== Page background: clean gray/white gradient ===== */
function PageBackdrop() {
    return (
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute inset-0 bg-zinc-950" />
            <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_50%_-10%,rgba(255,255,255,0.08),transparent_60%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(900px_500px_at_10%_20%,rgba(255,255,255,0.05),transparent_62%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(900px_500px_at_90%_35%,rgba(255,255,255,0.04),transparent_60%)]" />
            <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.0),rgba(0,0,0,0.75))]" />
        </div>
    );
}

/** ===== Section background ===== */
function SectionBackdrop() {
    return (
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(900px_420px_at_50%_0%,rgba(255,255,255,0.06),transparent_62%)]" />
            <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.02),transparent_65%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(800px_420px_at_50%_110%,rgba(255,255,255,0.02),transparent_60%)]" />
        </div>
    );
}

/** ===== TrioGlow: 제한된 시안/그레이 톤 ===== */
function TrioGlow({ className }: { className?: string }) {
    return (
        <div
            aria-hidden
            className={classNames(
                "pointer-events-none absolute inset-0 -z-10 overflow-hidden",
                className
            )}
        >
            <div className="absolute -top-28 left-1/2 h-[340px] w-[340px] -translate-x-1/2 rounded-full bg-cyan-400/12 blur-[100px]" />
            <div className="absolute top-20 -left-24 h-[360px] w-[360px] rounded-full bg-white/6 blur-[120px]" />
            <div className="absolute top-28 -right-24 h-[360px] w-[360px] rounded-full bg-cyan-500/8 blur-[120px]" />
        </div>
    );
}

/** ===== Unified hover lift (Apple-like) ===== */
const cardHover =
    "transition will-change-transform hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-25px_rgba(0,0,0,0.65)] hover:border-white/20";

function LangToggle({
    lang,
    setLang,
}: {
    lang: Lang;
    setLang: (l: Lang) => void;
}) {
    const common = i18n[lang].legal?.common;
    const koLabel = common?.langKo ?? "KOR";
    const enLabel = common?.langEn ?? "ENG";

    return (
        <div className="inline-flex items-center gap-1 rounded-2xl border border-white/10 bg-white/5 p-1">
            <button
                type="button"
                onClick={() => setLang("ko")}
                className={classNames(
                    "rounded-xl px-3 py-1.5 text-xs font-semibold transition",
                    lang === "ko"
                        ? "bg-white text-zinc-950"
                        : "text-white/70 hover:text-white hover:bg-white/10"
                )}
            >
                {koLabel}
            </button>
            <button
                type="button"
                onClick={() => setLang("en")}
                className={classNames(
                    "rounded-xl px-3 py-1.5 text-xs font-semibold transition",
                    lang === "en"
                        ? "bg-white text-zinc-950"
                        : "text-white/70 hover:text-white hover:bg-white/10"
                )}
            >
                {enLabel}
            </button>
        </div>
    );
}

function SectionTitle({
    kicker,
    title,
    subtitle,
}: {
    kicker?: string;
    title: string;
    subtitle?: string;
}) {
    return (
        <div className="mx-auto max-w-3xl text-center">
            {kicker ? (
                <div className="text-[11px] font-medium tracking-[0.22em] text-white/50">
                    {kicker}
                </div>
            ) : null}
            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-white md:text-4xl">
                {title}
            </h1>
            {subtitle ? (
                <p className="mt-3 text-sm leading-relaxed text-white/70 md:text-base">
                    {subtitle}
                </p>
            ) : null}
        </div>
    );
}

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-1 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
            <div className="text-[11px] uppercase tracking-widest text-white/50">
                {label}
            </div>
            <div className="text-sm text-white">{value}</div>
        </div>
    );
}

export default function TermsPage() {
    const [lang, setLangState] = useState<Lang>("ko");

    // ✅ PrivacyPage와 동일: pm_lang 공유
    useEffect(() => {
        const saved = (localStorage.getItem("pm_lang") as Lang | null) ?? "ko";
        setLangState(saved === "en" ? "en" : "ko");
    }, []);

    const setLang = (l: Lang) => {
        setLangState(l);
        localStorage.setItem("pm_lang", l);
    };

    const t = i18n[lang];
    const common = t.legal?.common;
    const terms = t.legal?.terms;

    // ✅ PrivacyPage와 동일한 방식으로 날짜 placeholder
    const effectiveDate = useMemo(() => "2026-07-20", []);
    const lastUpdated = useMemo(() => "2026-07-20", []);

    // i18n 누락 방어 (PrivacyPage엔 없지만, Terms는 개발중이라 넣어둠)
    const title = common?.termsTitle ?? (lang === "ko" ? "이용약관" : "Terms of Service");
    const intro =
        (lang === "ko"
            ? "본 약관은 Percent Map 서비스 이용과 관련한 권리, 의무 및 책임사항을 규정합니다."
            : "These terms govern your use of the Percent Map service.");

    const sections = terms?.sections ?? [];

    return (
        <div className="relative min-h-screen w-full bg-zinc-950 text-white">
            <PageBackdrop />

            {/* HEADER (PrivacyPage와 동일) */}
            <header className="sticky top-0 z-40 border-b border-white/10 bg-zinc-950/70 backdrop-blur">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-6">
                    <a href="/" className="flex items-center gap-2">
                        <PercentLogo className="h-8 w-8" />
                        <div className="font-brand text-2xl font-semibold tracking-tight leading-none">
                            Percent Map
                        </div>
                    </a>

                    <div className="flex items-center gap-2">
                        <LangToggle lang={lang} setLang={setLang} />
                    </div>
                </div>
            </header>

            {/* HERO (PrivacyPage와 동일한 카드/메타) */}
            <section className="relative border-b border-white/10">
                <SectionBackdrop />
                <div className="relative mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
                    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur md:p-10">
                        <TrioGlow />
                        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
                            <div className="max-w-2xl">
                                <div className="text-[11px] font-medium tracking-[0.22em] text-white/50">
                                    TERMS
                                </div>
                                <h1 className="mt-3 text-3xl font-semibold tracking-tight text-white md:text-4xl">
                                    {title}
                                </h1>
                                <p className="mt-4 text-sm leading-relaxed text-white/70 md:text-base">
                                    {intro}
                                </p>
                            </div>

                            <div className="grid w-full gap-3 md:max-w-sm">
                                <MetaRow
                                    label={common?.effectiveDateLabel ?? "Effective Date"}
                                    value={effectiveDate}
                                />
                                <MetaRow
                                    label={common?.lastUpdatedLabel ?? "Last Updated"}
                                    value={lastUpdated}
                                />
                                <MetaRow
                                    label={common?.contactLabel ?? "Contact"}
                                    value={
                                        <a
                                            className="text-white/90 underline decoration-white/25 underline-offset-4 hover:decoration-white/60"
                                            href={`mailto:${common?.contactEmail ?? "support@percentmap.com"}`}
                                        >
                                            {common?.contactEmail ?? "support@percentmap.com"}
                                        </a>
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CONTENT (PrivacyPage와 동일: 섹션 카드 반복) */}
            <section className="relative">
                <SectionBackdrop />
                <div className="relative mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
                    <SectionTitle
                        kicker={lang === "ko" ? "문서" : "DOCUMENT"}
                        title={title}
                        subtitle={
                            lang === "ko"
                                ? "아래 항목은 서비스 이용을 위한 기본 이용약관입니다."
                                : "The sections below describe our baseline Terms of Service."
                        }
                    />

                    <div className="mx-auto mt-10 grid max-w-4xl gap-4">
                        {sections.map((sec: any, idx: number) => (
                            <article
                                key={`${sec.title}-${idx}`}
                                className={classNames(
                                    "relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur md:p-7",
                                    cardHover
                                )}
                            >
                                <TrioGlow />
                                <h2 className="text-base font-semibold text-white md:text-lg">
                                    {sec.title ?? sec.heading}
                                </h2>
                                <div className="mt-3 space-y-2">
                                    {(sec.body ?? []).map((line: string, i: number) => (
                                        <p key={i} className="text-sm leading-relaxed text-white/70">
                                            {line}
                                        </p>
                                    ))}
                                </div>
                            </article>
                        ))}

                        {/* IMPORTANT NOTICE (Terms용: Hive on-chain) */}
                        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur">
                            <TrioGlow />
                            <div className="flex items-start gap-3">
                                <div className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-2xl border border-white/10 bg-zinc-950/30 text-sm">
                                    !
                                </div>
                                <div>
                                    <div className="text-sm font-semibold text-white">
                                        {lang === "ko" ? "중요 안내" : "Important Notice"}
                                    </div>
                                    <p className="mt-2 text-sm leading-relaxed text-white/70">
                                        {lang === "ko"
                                            ? "Hive 연동(또는 Hive Sync) 시 콘텐츠가 블록체인에 기록될 수 있으며, 블록체인의 특성상 삭제/수정이 제한될 수 있습니다. 또한 hive.blog 등 외부 서비스에 노출될 수 있으며 이는 서비스의 통제 범위를 벗어날 수 있습니다."
                                            : "When Hive linking (or Hive Sync) is enabled, content may be recorded on the blockchain. Due to blockchain characteristics, deletion/modification may be limited. Content may also appear on external services (e.g., hive.blog), which can be outside our control."}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer links (PrivacyPage와 동일: Back to home 포함) */}
                    <div className="mx-auto mt-10 flex max-w-4xl flex-col gap-3 border-t border-white/10 pt-6 text-sm md:flex-row md:items-center md:justify-between">
                        <a href="/" className="text-white/70 hover:text-white">
                            ← {lang === "ko" ? "홈으로" : "Back to Home"}
                        </a>

                        <div className="flex items-center gap-4">
                            {/* ✅ 여기서 Privacy 버튼/링크를 빼고 싶다고 했으니 "Terms만" 둠 */}
                            <a href="/privacy" className="text-white hover:text-white">
                                {common?.privacyTitle ?? "Privacy"}
                            </a>
                            <a href="/support" className="text-white/70 hover:text-white">
                                {lang === "ko" ? "고객 지원" : "Support"}
                            </a>
                            <a href="/account-deletion" className="text-white/70 hover:text-white">
                                {lang === "ko" ? "계정 삭제" : "Account deletion"}
                            </a>
                            {/* (원하면 아래 한 줄 자체를 삭제하면 됨 — 완전 제거 버전도 만들어줄 수 있음)
              <a href="/privacy" className="text-white/70 hover:text-white">
                {common?.privacyTitle ?? "Privacy"}
              </a>
              */}
                        </div>
                    </div>
                </div>
            </section>

            {/* FOOTER (PrivacyPage와 동일) */}
            <footer className="relative border-t border-white/10">
                <SectionBackdrop />
                <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
                    <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-center gap-3">
                            <PercentLogo className="h-7 w-7" />
                            <div>
                                <div className="text-sm font-semibold text-white/90">
                                    Percent Map
                                </div>
                                <div className="mt-1 text-xs text-white/55">
                                    {t.footer?.tagline ?? "Real Map for Real Travelers"}
                                </div>
                            </div>
                        </div>

                        <div className="text-sm text-white/70">
                            {common?.contactLabel ?? "Contact"}:{" "}
                            <a
                                className="text-white/80 hover:text-white"
                                href={`mailto:${common?.contactEmail ?? "support@percentmap.com"}`}
                            >
                                {common?.contactEmail ?? "support@percentmap.com"}
                            </a>
                        </div>
                    </div>

                    <div className="mt-8 flex flex-col gap-2 border-t border-white/10 pt-6 text-xs text-white/45 md:flex-row md:items-center md:justify-between">
                        <div>© {new Date().getFullYear()} Percent Map. All rights reserved.</div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
